import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Avatar,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Snackbar,
  SelectChangeEvent,
  Stack
} from '@mui/material';
import {
  Person as PersonIcon,
  Language as LanguageIcon,
  Notifications as NotificationsIcon,
  Palette as PaletteIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface UserSettings {
  language: string;
  theme: 'light' | 'dark';
  notifications: boolean;
}

const Profile: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [settings, setSettings] = useState<UserSettings>({
    language: i18n.language || 'en',
    theme: 'light',
    notifications: true
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  useEffect(() => {
    const savedSettings = localStorage.getItem('userSettings');
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      setSettings(parsed);
      if (parsed.language && parsed.language !== i18n.language) {
        i18n.changeLanguage(parsed.language);
      }
    }
  }, [i18n]);

  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    const newLanguage = event.target.value;
    setSettings({ ...settings, language: newLanguage });
    i18n.changeLanguage(newLanguage);
  };

  const handleThemeChange = (event: SelectChangeEvent<'light' | 'dark'>) => {
    setSettings({ ...settings, theme: event.target.value as 'light' | 'dark' });
  };

  const handleNotificationsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSettings({ ...settings, notifications: event.target.checked });
  };

  const handleSave = () => {
    try {
      localStorage.setItem('userSettings', JSON.stringify(settings));
      setSnackbarMessage(t('profile.success'));
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (error) {
      setSnackbarMessage(t('profile.error'));
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Avatar sx={{ width: 80, height: 80, mr: 3, bgcolor: 'primary.main' }}>
            <PersonIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" gutterBottom>
              {t('profile.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('profile.settings')}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Stack spacing={3}>
          <Box>
            <Box display="flex" alignItems="center" mb={2}>
              <LanguageIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h6">{t('profile.language')}</Typography>
            </Box>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="language-select-label">{t('profile.selectLanguage')}</InputLabel>
              <Select
                labelId="language-select-label"
                id="language-select"
                value={settings.language}
                onChange={handleLanguageChange}
                label={t('profile.selectLanguage')}
              >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="ru">Русский</MenuItem>
                <MenuItem value="es">Español</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Box display="flex" alignItems="center" mb={2}>
              <PaletteIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h6">{t('profile.theme')}</Typography>
            </Box>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="theme-select-label">{t('profile.theme')}</InputLabel>
              <Select
                labelId="theme-select-label"
                id="theme-select"
                value={settings.theme}
                onChange={handleThemeChange}
                label={t('profile.theme')}
              >
                <MenuItem value="light">Light</MenuItem>
                <MenuItem value="dark">Dark</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Box display="flex" alignItems="center" mb={2}>
              <NotificationsIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h6">{t('profile.notifications')}</Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.notifications}
                  onChange={handleNotificationsChange}
                  color="primary"
                />
              }
              label={settings.notifications ? 'Enabled' : 'Disabled'}
            />
          </Box>

          <Box>
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                size="large"
              >
                {t('profile.save')}
              </Button>
            </Box>
          </Box>
        </Stack>
      </Paper>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Profile;